import { addDays, addMinutes, format, startOfDay } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import type {
  Availability,
  DateOverride,
  Weekday,
  WeeklyBlock,
} from '@/types/availability'

export interface SlotParams {
  duration_minutes: number
  buffer_minutes: number
  min_lead_minutes: number
  window_days: number
}

export const DEFAULT_SLOT_PARAMS: SlotParams = {
  duration_minutes: 20,
  buffer_minutes: 5,
  min_lead_minutes: 120,
  window_days: 28,
}

interface TimeRange {
  start: string
  end: string
}

export interface ComputedSlot {
  start_at: string
  end_at: string
}

function sortRanges(ranges: TimeRange[]): TimeRange[] {
  return [...ranges].sort((a, b) => a.start.localeCompare(b.start))
}

function subtractRange(base: TimeRange[], cut: TimeRange | null): TimeRange[] {
  if (!cut || !cut.start || !cut.end) {
    return []
  }
  const out: TimeRange[] = []
  for (const b of base) {
    if (cut.end <= b.start || cut.start >= b.end) {
      out.push(b)
      continue
    }
    if (cut.start > b.start) {
      out.push({ start: b.start, end: cut.start < b.end ? cut.start : b.end })
    }
    if (cut.end < b.end) {
      out.push({ start: cut.end > b.start ? cut.end : b.start, end: b.end })
    }
  }
  return out.filter((r) => r.start < r.end)
}

export function applyOverridesForDate(
  weekdayBlocks: WeeklyBlock[],
  overrides: DateOverride[],
): TimeRange[] {
  let ranges: TimeRange[] = weekdayBlocks.map((b) => ({ start: b.start_time, end: b.end_time }))

  for (const ov of overrides) {
    if (ov.kind === 'block') {
      if (ov.start_time && ov.end_time) {
        ranges = subtractRange(ranges, { start: ov.start_time, end: ov.end_time })
      } else {
        ranges = []
      }
    } else if (ov.kind === 'extra' && ov.start_time && ov.end_time) {
      ranges.push({ start: ov.start_time, end: ov.end_time })
    }
  }

  return sortRanges(ranges)
}

function weekdayFor(date: Date): Weekday {
  return date.getDay() as Weekday
}

/**
 * Compute available slots for a provider over a date range.
 *
 * - Recurring weekly blocks are interpreted in the provider's IANA timezone.
 * - Date overrides either subtract from recurring blocks (`block`) or append
 *   extra time ranges (`extra`).
 * - Slots have fixed `duration_minutes` and are separated by `buffer_minutes`.
 * - Slots starting within `min_lead_minutes` of `now` are excluded.
 * - `overlapsBooked` lets callers exclude already-booked or held ranges.
 * - DST is handled correctly because we build each day's start/end in the
 *   provider tz via `fromZonedTime`, which reads the local UTC offset for
 *   that wall-clock moment.
 *
 * This is the source of truth for slot math. The MSW handler in
 * `patient-portal/src/test/mocks/handlers/scheduling.ts` mirrors this algorithm
 * so the booking flow works end-to-end before the backend lands; keep the two
 * in sync until the server takes over.
 */
export function computeSlots(opts: {
  availability: Availability
  providerTimezone: string
  from: Date
  to: Date
  now: Date
  params?: SlotParams
  overlapsBooked?: (start: Date, end: Date) => boolean
}): ComputedSlot[] {
  const params = opts.params ?? DEFAULT_SLOT_PARAMS
  const duration = params.duration_minutes
  const stride = duration + params.buffer_minutes
  const leadCutoff = addMinutes(opts.now, params.min_lead_minutes)

  const out: ComputedSlot[] = []
  const cursorDay = startOfDay(opts.from)
  const endDay = startOfDay(opts.to)

  for (let d = cursorDay; d.getTime() <= endDay.getTime(); d = addDays(d, 1)) {
    const dateStr = format(d, 'yyyy-MM-dd')
    const weekdayBlocks = opts.availability.recurring_blocks.filter(
      (b) => b.weekday === weekdayFor(d),
    )
    const dayOverrides = opts.availability.date_overrides.filter((o) => o.date === dateStr)
    const ranges = applyOverridesForDate(weekdayBlocks, dayOverrides)

    for (const range of ranges) {
      const rangeStart = fromZonedTime(`${dateStr}T${range.start}:00`, opts.providerTimezone)
      const rangeEnd = fromZonedTime(`${dateStr}T${range.end}:00`, opts.providerTimezone)
      let cursor = rangeStart
      while (addMinutes(cursor, duration).getTime() <= rangeEnd.getTime()) {
        const slotEnd = addMinutes(cursor, duration)
        const tooSoon = cursor.getTime() < leadCutoff.getTime()
        const booked = opts.overlapsBooked?.(cursor, slotEnd) ?? false
        if (!tooSoon && !booked) {
          out.push({ start_at: cursor.toISOString(), end_at: slotEnd.toISOString() })
        }
        cursor = addMinutes(cursor, stride)
      }
    }
  }

  return out
}
